import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

const Field = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "baseline",
      gap: 1,
      py: 0.35,
      minWidth: 0,
    }}
  >
    <Typography variant="caption" sx={{ color: "text.secondary", flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography
      variant="caption"
      sx={{ color: "text.primary", fontWeight: 500, textAlign: "right", wordBreak: "break-word" }}
    >
      {value === undefined || value === null || value === "" ? "—" : String(value)}
    </Typography>
  </Box>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
};

/** Two-column field grid on sm+; single column on narrow screens — same for all entity types. */
const FieldsGrid = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
      columnGap: 2,
      rowGap: 0,
      alignContent: "start",
    }}
  >
    {children}
  </Box>
);

FieldsGrid.propTypes = {
  children: PropTypes.node,
};

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString();
  } catch {
    return null;
  }
};

const resolveAccountId = (entityType, account) => {
  if (entityType === "users") return account.user_id;
  if (entityType === "law_firms") return account.lawfirm_id;
  return account.pg_id;
};

const getAccountTitle = (entityType, account, accountId) => {
  if (entityType === "users") {
    const fullName = [account.first_name, account.last_name].filter(Boolean).join(" ");
    return fullName || account.username || account.email || `User ${accountId}`;
  }
  if (entityType === "law_firms") {
    return account.name || `Law Firm ${accountId}`;
  }
  return account.full_name || account.business_name || `PG ${accountId}`;
};

const getAccountSubtitle = (entityType, account) => {
  if (entityType === "users") return account.email;
  if (entityType === "law_firms") {
    return account.customer_number ? `Customer #${account.customer_number}` : null;
  }
  return account.email_address;
};

const getCardBorder = (isKeeper, isSoftDeleted) => {
  if (isKeeper) return "2px solid #2563eb";
  if (isSoftDeleted) return "1px dashed rgba(0,0,0,0.25)";
  return "1px solid rgba(0,0,0,0.12)";
};

const getCardBackgroundColor = (isKeeper, isSoftDeleted) => {
  if (isKeeper) return "rgba(37,99,235,0.06)";
  if (isSoftDeleted) return "rgba(0,0,0,0.04)";
  return "#ffffff";
};

const getCardSx = (isKeeper, isSoftDeleted, selectable) => ({
  position: "relative",
  borderRadius: 2,
  border: getCardBorder(isKeeper, isSoftDeleted),
  backgroundColor: getCardBackgroundColor(isKeeper, isSoftDeleted),
  opacity: isSoftDeleted ? 0.7 : 1,
  cursor: selectable ? "pointer" : "default",
  transition: "border-color 120ms ease, background-color 120ms ease, box-shadow 120ms ease",
  "&:hover": selectable
    ? { borderColor: "#2563eb", boxShadow: "0 1px 6px rgba(37,99,235,0.15)" }
    : undefined,
  "&:focus-visible": selectable
    ? { outline: "2px solid #2563eb", outlineOffset: 2 }
    : undefined,
  height: "100%",
});

const getCardAccessibilityProps = (selectable, isKeeper) => ({
  role: selectable ? "button" : undefined,
  tabIndex: selectable ? 0 : -1,
  ariaPressed: isKeeper || undefined,
});


const renderKeeperIndicatorIcon = (isKeeper, isSoftDeleted) =>
  isKeeper ? (
    <CheckCircleRoundedIcon fontSize="small" sx={{ color: "#2563eb", mt: "2px" }} />
  ) : (
    <RadioButtonUncheckedIcon
      fontSize="small"
      sx={{ color: isSoftDeleted ? "text.disabled" : "text.secondary", mt: "2px" }}
    />
  );

const renderKeeperChip = (isKeeper) =>
  isKeeper ? <Chip size="small" label="Keeper" color="primary" sx={{ height: 22 }} /> : null;

const renderSubtitle = (subtitle) =>
  subtitle ? (
    <Typography variant="caption" sx={{ color: "text.secondary" }}>{subtitle}</Typography>
  ) : null;

const renderRecommendedChip = (isRecommended) =>
  isRecommended ? (
    <Chip size="small" label="Recommended" color="success" variant="outlined" />
  ) : null;

const renderSoftDeletedChip = (isSoftDeleted) =>
  isSoftDeleted ? (
    <Tooltip title="This account is already soft-deleted (likely from an earlier merge) and cannot be selected.">
      <Chip size="small" label="Soft-deleted" color="warning" variant="outlined" />
    </Tooltip>
  ) : null;

const renderEntityFields = (entityType, account) => {
  if (entityType === "users") {
    return (
      <FieldsGrid>
        <Field label="Username" value={account.username} />
        <Field label="Role" value={account.user_role} />
        <Field label="Law firm" value={account.law_firm_name} />
        <Field label="Position" value={account.lawfirm_user_position} />
        <Field
          label="Bonds (req / firm)"
          value={`${account.bonds_as_requester || 0} / ${account.bonds_as_lawfirm_user || 0}`}
        />
        <Field label="PG record" value={account.pg_full_name || (account.pg_id ? `pg_id ${account.pg_id}` : null)} />
        <Field label="Last login" value={formatDate(account.last_login)} />
        <Field label="Created" value={formatDate(account.created_at)} />
        <Field label="Tokens" value={account.token_count} />
      </FieldsGrid>
    );
  }

  if (entityType === "law_firms") {
    return (
      <FieldsGrid>
        <Field label="Customer number" value={account.customer_number} />
        <Field label="Active" value={account.is_active ? "Yes" : "No"} />
        <Field label="Bonds (own / managing)" value={`${account.bond_count || 0} / ${account.managing_bond_count || 0}`} />
        <Field label="Locations" value={account.location_count} />
        <Field label="User mappings" value={account.user_mapping_count} />
        <Field label="Created" value={formatDate(account.created_at)} />
        <Field label="Updated" value={formatDate(account.updated_at)} />
      </FieldsGrid>
    );
  }

  if (entityType === "pgs") {
    return (
      <FieldsGrid>
        <Field label="Business name" value={account.business_name} />
        <Field label="Email" value={account.email_address} />
        <Field label="Phone" value={account.cell_phone} />
        <Field label="Portal username" value={account.portal_username} />
        <Field label="Portal email" value={account.portal_email} />
        <Field label="Total bonds" value={account.total_bonds} />
        <Field label="Bond guardians" value={account.bond_guardian_count} />
        <Field label="Blanket bonds" value={account.blanket_bond_count} />
        <Field label="PG contacts" value={account.pg_contact_count} />
        <Field label="Created" value={formatDate(account.created_at)} />
      </FieldsGrid>
    );
  }

  return null;
};


const renderSoftDeletedNotice = (isSoftDeleted) =>
  isSoftDeleted ? (
    <Typography
      variant="caption"
      sx={{ display: "block", mt: 1.5, color: "text.disabled", fontStyle: "italic" }}
    >
      Already removed — not available as keeper or merge target.
    </Typography>
  ) : null;

/**
 * One account inside a duplicate group.
 *
 * Interaction model
 * - Click anywhere on an active card to set it as the keeper.
 * - Merge actions live on the duplicate group row (toolbar / per-loser buttons → preview).
 * - Soft-deleted cards are read-only — already removed by a previous merge.
 */
const AccountDetailCard = ({
  account,
  entityType,
  isKeeper,
  isRecommended,
  onSelectAsKeeper,
}) => {
  const accountId = resolveAccountId(entityType, account);
  const isSoftDeleted = !!account.deleted_at;
  const selectable = !isSoftDeleted && !isKeeper && typeof onSelectAsKeeper === "function";

  const title = getAccountTitle(entityType, account, accountId);
  const subtitle = getAccountSubtitle(entityType, account);

  const handleCardClick = () => {
    if (!selectable) return;
    onSelectAsKeeper(accountId);
  };

  const handleCardKey = (e) => {
    if (!selectable) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelectAsKeeper(accountId);
    }
  };

  const a11yProps = getCardAccessibilityProps(selectable, isKeeper);

  const card = (
    <Card
      elevation={0}
      role={a11yProps.role}
      tabIndex={a11yProps.tabIndex}
      aria-pressed={a11yProps.ariaPressed}
      onClick={handleCardClick}
      onKeyDown={handleCardKey}
      sx={getCardSx(isKeeper, isSoftDeleted, selectable)}
    >
      <CardContent sx={{ "&:last-child": { pb: 2 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1, gap: 1 }}>
          <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0, flex: 1 }}>
            {renderKeeperIndicatorIcon(isKeeper, isSoftDeleted)}
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ mb: 0.25 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {title}
                </Typography>
                {renderKeeperChip(isKeeper)}
              </Stack>
              {renderSubtitle(subtitle)}
            </Box>
          </Stack>
          <Stack spacing={0.5} alignItems="flex-end">
            <Chip size="small" label={`ID ${accountId}`} variant="outlined" />
            {renderRecommendedChip(isRecommended)}
            {renderSoftDeletedChip(isSoftDeleted)}
          </Stack>
        </Box>

        {renderEntityFields(entityType, account)}

        {renderSoftDeletedNotice(isSoftDeleted)}
      </CardContent>
    </Card>
  );

  return card;
};

AccountDetailCard.propTypes = {
  account: PropTypes.shape({
    user_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    lawfirm_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    pg_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    username: PropTypes.string,
    email: PropTypes.string,
    email_address: PropTypes.string,
    name: PropTypes.string,
    full_name: PropTypes.string,
    business_name: PropTypes.string,
    customer_number: PropTypes.string,
    deleted_at: PropTypes.string,
    user_role: PropTypes.string,
    law_firm_name: PropTypes.string,
    lawfirm_user_position: PropTypes.string,
    bonds_as_requester: PropTypes.number,
    bonds_as_lawfirm_user: PropTypes.number,
    pg_full_name: PropTypes.string,
    last_login: PropTypes.string,
    created_at: PropTypes.string,
    updated_at: PropTypes.string,
    token_count: PropTypes.number,
    is_active: PropTypes.bool,
    bond_count: PropTypes.number,
    managing_bond_count: PropTypes.number,
    location_count: PropTypes.number,
    user_mapping_count: PropTypes.number,
    cell_phone: PropTypes.string,
    portal_username: PropTypes.string,
    portal_email: PropTypes.string,
    total_bonds: PropTypes.number,
    bond_guardian_count: PropTypes.number,
    blanket_bond_count: PropTypes.number,
    pg_contact_count: PropTypes.number,
  }).isRequired,
  entityType: PropTypes.oneOf(["users", "law_firms", "pgs"]).isRequired,
  isKeeper: PropTypes.bool,
  isRecommended: PropTypes.bool,
  onSelectAsKeeper: PropTypes.func,
};

export default AccountDetailCard;
